import { AnimatePresence } from 'framer-motion';
import { useMountedState, usePrevious } from 'react-use';
import type { DraftMode } from '@/pages/Schedule/stores';
import { useEntryModalContext } from '../../contexts';
import type { EntryModalMessage } from '../../types';
import { PanelLayout } from './components/PanelLayout';
import { MessagesPanel } from './panels/MessagesPanel';
import { RsvpsPanel } from './panels/RsvpsPanel';

type Props = {
  mode?: DraftMode;
  messages: EntryModalMessage[];
  onSave: () => Promise<void>;
};

export const EntryModalSidebar = ({ messages, onSave }: Props) => {
  const { activeSidebarPanel } = useEntryModalContext();
  const prevActiveSidebarPanel = usePrevious(activeSidebarPanel);
  const isMounted = useMountedState();

  const isSwitchingPanels =
    activeSidebarPanel &&
    prevActiveSidebarPanel &&
    activeSidebarPanel !== prevActiveSidebarPanel;

  // when a panel is mounted, start it in the open state if another panel is
  // already open (because the user is switching panels) or if the modal is
  // being opened for the first time.
  const defaultOpen = isSwitchingPanels || !isMounted();

  return (
    <AnimatePresence>
      <PanelLayout
        data-testid="sidebar"
        defaultOpen={defaultOpen}
        key={activeSidebarPanel}
      >
        {activeSidebarPanel === 'messages' ? (
          <MessagesPanel
            data-testid="sidebar-messages"
            messages={messages}
            onBeforeSaveMessage={onSave}
          />
        ) : activeSidebarPanel === 'rsvps' ? (
          <RsvpsPanel data-testid="sidebar-rsvps" onBeforeToggleRsvp={onSave} />
        ) : null}
      </PanelLayout>
    </AnimatePresence>
  );
};
